import React, { useEffect, useMemo, useRef, useState } from 'react';

const fallbackSlides = [
  {
    id: 'fallback-photovoltaik-konstanz',
    imageUrl: '/seo-assets/photovoltaik-installation-konstanz.jpg',
    alt: 'Photovoltaik Installation von EOS Energy Systems in der Region Konstanz, Stockach, Radolfzell und Singen',
    titleText: 'Photovoltaik und Elektrotechnik in Konstanz, Stockach, Radolfzell und Singen',
    headline: ['Für Energie,', 'die ruhig läuft.'],
    subline:
      'Elektrotechnik, Photovoltaik, Speicher und Ladeinfrastruktur – sauber geplant, fachgerecht umgesetzt und nachvollziehbar dokumentiert.',
    cta: { label: 'Projekt besprechen', targetId: 'contact' },
    secondaryCta: { label: 'Leistungen ansehen', targetId: 'features' },
  },
];

const SLIDE_DURATION = 10000;

function normalizeSlides(slidesProp) {
  const source = Array.isArray(slidesProp) && slidesProp.length > 0 ? slidesProp : fallbackSlides;

  return source.map((slide, index) => ({
    id: slide.id ?? `${slide.imageUrl}-${index}`,
    imageUrl: slide.imageUrl,
    alt:
      slide.alt ||
      `EOS Energy Systems ${index + 1}: Elektrotechnik und Photovoltaik in Konstanz, Stockach, Radolfzell und Singen`,
    titleText:
      slide.titleText ||
      'EOS Energy Systems',
    headline: Array.isArray(slide.headline) ? slide.headline : [slide.headline || 'Für Energie,', 'die ruhig läuft.'],
    subline: slide.subline || fallbackSlides[0].subline,
    cta: slide.cta || fallbackSlides[0].cta,
    secondaryCta: slide.secondaryCta || { label: 'Leistungen ansehen', targetId: 'features' },
    footerLine: slide.footerLine,
  }));
}

export default function HeroSlider({ company, scrollToSection, slides: slidesProp }) {
  const slides = useMemo(() => normalizeSlides(slidesProp), [slidesProp]);
  const heroRef = useRef(null);
  const intervalRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);


  useEffect(() => {
    const update = () => {
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const max = Math.max(1, rect.height - window.innerHeight);
      const next = Math.max(0, Math.min(1, -rect.top / max));
      setProgress(next);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    intervalRef.current = window.setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, SLIDE_DURATION);

    return () => window.clearInterval(intervalRef.current);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    if (slides.length > 1) {
      intervalRef.current = window.setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, SLIDE_DURATION);
    }
  };

  const activeSlide = slides[currentSlide] || slides[0];
  const primaryCta = activeSlide.cta;
  const secondaryCta = activeSlide.secondaryCta;

  return (
    <section
      id="hero"
      data-bg="dark"
      ref={heroRef}
      className="eos-experience-hero"
      style={{ '--hero-progress': progress }}
      aria-label={activeSlide.titleText}
    >
      <div className="eos-experience-hero-media">
        {slides.map((slide, index) => (
          <img
            key={slide.id}
            src={slide.imageUrl}
            alt={slide.alt}
            title={slide.titleText}
            className={currentSlide === index ? 'is-active' : ''}
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : 'auto'}
          />
        ))}
      </div>

      <div className="eos-experience-hero-content">
        <div key={`copy-${activeSlide.id}`} className="eos-experience-hero-copy">
          <div className="eos-experience-kicker">{activeSlide.titleText}</div>
          <h1>
            {activeSlide.headline.map((line, index) => (
              <React.Fragment key={line}>
                {line}{index < activeSlide.headline.length - 1 ? ' ' : ''}
                {index < activeSlide.headline.length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p>{activeSlide.subline}</p>

          <div className="eos-experience-hero-actions">
            <button
              type="button"
              className="eos-experience-pill"
              onClick={() => scrollToSection?.(primaryCta?.targetId || 'contact')}
              aria-label={`${primaryCta?.label || 'Projekt besprechen'} – EOS Energy Systems in Konstanz, Stockach, Radolfzell und Singen`}
            >
              {primaryCta?.label || 'Projekt besprechen'}
            </button>
            <button
              type="button"
              className="eos-experience-pill is-ghost"
              onClick={() => scrollToSection?.(secondaryCta?.targetId || 'features')}
              aria-label={`${secondaryCta?.label || 'Leistungen ansehen'} – Photovoltaik, Elektrotechnik, Speicher und Wallbox`}
            >
              {secondaryCta?.label || 'Leistungen ansehen'}
            </button>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <div className="eos-experience-hero-dots" aria-label="Hero Bilder und Leistungen auswählen">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goToSlide(index)}
              className={currentSlide === index ? 'is-active' : ''}
              aria-label={`${slide.titleText} anzeigen`}
              title={slide.titleText}
            />
          ))}
        </div>
      )}

      <span className="sr-only">{activeSlide.alt}</span>
    </section>
  );
}
